/**
 * Commission reports API client. Talks to the real backend.
 *
 * Endpoints are org-scoped: the caller supplies the org UUID, which lives
 * in the auth state from the login response. Once the backend moves to
 * pulling org context from the JWT, this module will drop the orgUuid
 * argument and the paths will shorten.
 */
import api from './client';

const base = (orgUuid) => `/organisations/${orgUuid}/commission-runs`;

export const commissionReportsApi = {
    listYears(orgUuid) {
        return api.get(`${base(orgUuid)}/years`).then((r) => r.data);
    },

    listMonths(orgUuid, year) {
        return api.get(base(orgUuid), { params: { year } }).then((r) => r.data);
    },

    getMonth(orgUuid, year, month) {
        return api.get(`${base(orgUuid)}/${year}/${month}`).then((r) => r.data);
    },

    /**
     * Upload one input file for a month. Auto-creates the run record on
     * the server side if this is the first upload for that month.
     *
     * @param file native File from an <input type="file"> change event
     * @param kind discriminator string; must match a value in
     *   {@link com.commissioner.service.CommissionRunService} —
     *   one of 'CRM', 'ADHOC', 'LENDER_UPFRONT', 'LENDER_TRAIL'. Anything
     *   else is uploaded but won't be picked up by the engine.
     * @param onProgress optional (loaded, total) => void called during upload
     */
    uploadInputFile(orgUuid, year, month, file, kind, onProgress) {
        const formData = new FormData();
        formData.append('file', file);

        return api
            .post(`${base(orgUuid)}/${year}/${month}/input-files`, formData, {
                params: { kind },
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    if (onProgress && e.total) onProgress(e.loaded, e.total);
                },
            })
            .then((r) => r.data);
    },

    deleteInputFile(orgUuid, year, month, fileId) {
        return api
            .delete(`${base(orgUuid)}/${year}/${month}/input-files/${fileId}`)
            .then((r) => r.data);
    },

    /**
     * Download a file (input or output) for the given month, streamed
     * back through the application — bytes never leave the API
     * boundary, no presigned S3 URLs exposed to the browser. Same auth
     * rules as every other endpoint.
     *
     * <p>Returns the axios response so the caller can read both the
     * blob and the {@code Content-Disposition} header (which carries
     * the canonical filename). The browser's {@code download} attribute
     * picks up that filename when we trigger the save via an anchor
     * click.
     */
    downloadFile(orgUuid, year, month, fileId) {
        return api.get(
            `${base(orgUuid)}/${year}/${month}/files/${fileId}/download`,
            { responseType: 'blob' });
    },

    /**
     * Execute the commission engine for the given month. Pulls the input
     * files from S3, runs the calc, writes the output xlsx back to S3,
     * and returns the updated run state (status flipped to COMPLETED or
     * FAILED, with the new output file in `outputFiles`).
     *
     * <p>Backend returns 202 Accepted — execution is synchronous today
     * but the response is treated as if it were async, so callers should
     * inspect `response.status` (not the HTTP code) to decide what
     * happened. A COMPLETED response means the output file is ready to
     * download immediately; a FAILED response carries a `failureReason`.
     *
     * <p>Rates are not sent per-run. The backend uses the organisation's
     * saved rate config (set under Configuration → Rates). If no config
     * has been saved, the backend responds 400 with a message.
     */
    executeRun(orgUuid, year, month) {
        return api
            .post(`${base(orgUuid)}/${year}/${month}/execute`)
            .then((r) => r.data);
    },
};